import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import type { PipelinesConnection } from '@/services/db/integration/entity';
import { getPipelinesConnectionsQueryOptions } from '@/services/db/integration/queries';
import { useIntegrationsWorkspaceId } from '../workspace-id';
import { useIntegrationsListSources } from '../directory/hooks';
import { buildIntegrationsList } from '../directory/synthesize';
import { useConnectionSources } from './sources';
import { buildConnectionsList, buildPipelinesConnectionsById } from './view-model/build';
import type { IntegrationConnection } from './view-model/types';

const EMPTY_PIPELINES: readonly PipelinesConnection[] = [];

/**
 * The Connections tab view model: fan out the synthesized-list sources, the
 * overview-only data sources, and the pipelines connections, then assemble the
 * flat, sorted connection list. `urlParamsEnabled` / `isTwilioWhiteLabeled` are
 * wired into the synthesized list here (the directory leaves them defaulted).
 *
 * Every source (including pipelines) is a non-suspense `useQuery` that degrades
 * to its "absent" default on error — matching the Ember overview, where each
 * fan-out task settles independently and a 500 renders the page with whatever
 * resolved rather than blanking it. `isLoading` is true only while a source is
 * still pending; a settled error renders with the empty default. The pipelines
 * read keeps `staleTime: 0`, so re-entering the route (e.g. returning from the
 * Pipelines app) refetches on mount.
 */
export function useConnectionsViewModel(): {
  connections: IntegrationConnection[];
  /** Every fetched pipelines connection by wire id, for the Connections column lookup. */
  pipelinesConnectionsById: ReadonlyMap<string, IntegrationConnection>;
  isLoading: boolean;
  shouldGroupAutoTrack: boolean;
} {
  const workspaceIdNumber = useIntegrationsWorkspaceId();
  const workspaceId = String(workspaceIdNumber);
  const queryClient = useQueryClient();
  const { sources: listSources, config: listConfig, isLoading: listLoading } = useIntegrationsListSources();
  const { sources: connectionSources, isLoading: connectionLoading } = useConnectionSources();
  const pipelines = useQuery(getPipelinesConnectionsQueryOptions(workspaceIdNumber, queryClient));

  const isTwilioWhiteLabeled = useFeatureFlag(AccountFeatureFlag.WhiteLabelTwilio);
  const trackEnabled = useFeatureFlag(AccountFeatureFlag.CdpTrackEnabled);
  const passthroughEnabled = useFeatureFlag(AccountFeatureFlag.CdpPassthroughEnabled);
  const areAutoTrackSourcesEnabled = trackEnabled || passthroughEnabled;

  const pipelinesData = pipelines.data ?? EMPTY_PIPELINES;

  const connections = useMemo(() => {
    const synthesizedIntegrations = buildIntegrationsList(listSources, {
      ...listConfig,
      urlParamsEnabled: connectionSources.urlParams.enabled,
      isTwilioWhiteLabeled,
    });
    return buildConnectionsList({
      synthesizedIntegrations,
      pipelinesConnections: pipelinesData,
      catalogIntegrations: listSources.backendIntegrations,
      sources: connectionSources,
      config: { workspaceId, areAutoTrackSourcesEnabled },
    });
  }, [
    listSources,
    listConfig,
    connectionSources,
    pipelinesData,
    isTwilioWhiteLabeled,
    areAutoTrackSourcesEnabled,
    workspaceId,
  ]);

  const pipelinesConnectionsById = useMemo(
    () => buildPipelinesConnectionsById(pipelinesData, listSources.backendIntegrations),
    [pipelinesData, listSources.backendIntegrations],
  );

  return {
    connections,
    pipelinesConnectionsById,
    isLoading: listLoading || connectionLoading || pipelines.isLoading,
    shouldGroupAutoTrack: areAutoTrackSourcesEnabled,
  };
}
